// Per-item props for Vuetify 3's select family -- the value bound to `:item-props`.
//
// WHY THIS EXISTS: Vuetify 2 read a per-item `disabled` off the TOP LEVEL of a select
// item; its `item-disabled` prop defaulted to the key 'disabled', so it worked without
// anyone ever writing the prop down. Vuetify 3 DELETED `item-disabled` and reads per-item
// props ONLY from `item.props` (`item-props`, default key 'props'). See
// vuetify/lib/composables/list-items.js `transformItem()`, which builds
// `_props = { title, value, ...getPropertyFromItem(item, props.itemProps) }`, and
// VSelect.js / VAutocomplete.js / VCombobox.js, each of which guards selection with
// `if (item.props.disabled) return`.
//
// The consequence was silent and total: 103 `disabled: true` flags across the ten
// services/selectLists/*Statii.ts files -- the statuses only program logic is allowed to
// set (Closed, CLOSED WON, Service Accepted, Suspended, Disconnected, ...) -- rendered
// fully enabled and selectable. Nothing warned and nothing threw; the dropdown just
// quietly did more than it was allowed to. Nothing was lost at the Nuxt 4 cutover either:
// no .vue file ever bound `item-disabled`, because V2's default made it unnecessary. The
// changed default IS the regression.
//
// THIS IS A RENDER-LAYER BRIDGE ON PURPOSE, not a data migration:
//   - the app's own logic reads the TOP-LEVEL key -- EntityMaskForm.selectAllItems and
//     selectFieldRules.shouldCloseMenuOnAllSelected both filter `!i.disabled`, and
//     modules/setOpportunityMasterServiceServicesToSelectedOpenStatus.ts filters
//     `isAnyOpenOpportunityMSServiceStatus(s) && !s.disabled`, which is the app's own
//     definition of "manually settable". Top-level `disabled` stays the canonical
//     AUTHORING shape;
//   - most of these fields are `doReturnObject: true`, so the selected item object IS the
//     API request body (see the note at getSelectList.ts). Adding keys to the item DATA
//     leaks them into payloads;
//   - services/selectLists/_common.ts getItemByLabelFactory / getItemByIdFactory hand the
//     item object itself back as a field `default:` value.
//
// MERGES rather than replaces `item.props`: services/suppliers.ts and services/products.ts
// emit `{ type: 'divider', props: { inset: true } }`, and getSelectList.ts already emits
// BOTH shapes for service-fetched lists. Replacing would un-inset every divider.
//
// Effective rule: an item is disabled if EITHER shape says so.
export function selectItemProps(
  item: unknown
): Record<string, unknown> | undefined {
  // Vuetify hands this raw primitives: VCombobox free text, and the plain string[] that
  // addressFieldsCommonNoDividers.ts assigns from Smarty suggestions. getPropertyFromItem
  // (vuetify/lib/util/helpers.js) takes the primitive branch but STILL invokes a
  // function-valued item-props, so `item.props` here would throw on null and read off a
  // String wrapper otherwise.
  if (item === null || typeof item !== 'object') {
    return undefined
  }

  const { props, disabled } = item as { props?: unknown; disabled?: unknown }
  const ownProps =
    props !== null && typeof props === 'object'
      ? (props as Record<string, unknown>)
      : undefined

  if (!disabled) {
    // Exactly what Vuetify's default `item-props: 'props'` would have produced. Returned
    // by reference, so the common path allocates nothing.
    return ownProps
  }
  return { ...ownProps, disabled: true }
}
